/** Local password strength estimate — never sent anywhere. */
export type StrengthLevel = 'very-weak' | 'weak' | 'fair' | 'strong' | 'excellent';

export function estimatePasswordStrength(password: string): {
  level: StrengthLevel;
  score: number;
  hints: string[];
} {
  const hints: string[] = [];
  let score = 0;
  if (password.length >= 8) score += 1;
  else hints.push('Use at least 8 characters');
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (password.length >= 20) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  else hints.push('Mix upper and lower case');
  if (/\d/.test(password)) score += 1;
  else hints.push('Add digits');
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  else hints.push('Add symbols');

  let level: StrengthLevel;
  if (score <= 2) level = 'very-weak';
  else if (score <= 3) level = 'weak';
  else if (score <= 5) level = 'fair';
  else if (score <= 6) level = 'strong';
  else level = 'excellent';

  if (password.length === 0) {
    return { level: 'very-weak', score: 0, hints: ['Enter a password'] };
  }
  return { level, score, hints: hints.slice(0, 2) };
}

const ALPHABET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_';

/** Cryptographically secure passphrase generator (not a recovery backdoor). */
export function generatePassphrase(length = 32): string {
  if (length < 16 || length > 128) {
    throw new Error('Passphrase length must be 16–128');
  }
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < length; i++) {
    out += ALPHABET[bytes[i]! % ALPHABET.length]!;
  }
  return out;
}
